import { Link } from 'react-router-dom';
import { isDevAuth } from '../lib/sso.js';
import { KeyRound, ShieldAlert } from 'lucide-react';
import { ROLE_LABEL, AREA_MIN_ROLE, type Area } from '../lib/access.js';
import type { TenantRole } from '@esa/shared';
import { Button } from './Button.js';
import { Badge } from './Badge.js';

/**
 * Shown when a page is opened in a group where the current identity lacks the
 * role for it — a direct URL, a stale tab, or a group switch.
 *
 * It names the role you have, the role the page needs, and the group, because
 * "missing capability config.write in tenant scadmin" is a server error
 * message and not something anybody should be reading in a product.
 */
export function AccessDenied({
  area,
  role,
  tenantName,
}: {
  area: Area;
  role: string | null | undefined;
  tenantName: string | undefined;
}) {
  const needed = AREA_MIN_ROLE[area];

  return (
    <div className="mx-auto flex max-w-lg flex-col items-center px-6 py-20 text-center">
      <span
        aria-hidden
        className="mb-4 flex h-11 w-11 items-center justify-center rounded-lg border border-line bg-surface-sunken text-ink-tertiary"
      >
        <ShieldAlert className="h-5 w-5" />
      </span>

      <h1 className="text-base font-semibold text-ink">Not available in this group</h1>

      <p className="mt-2 text-sm leading-relaxed text-ink-secondary">
        In <strong className="text-ink">{tenantName ?? 'this group'}</strong> you are a{' '}
        <Badge tone="neutral">{role ? ROLE_LABEL[role as TenantRole] ?? role : 'requester'}</Badge>.
        This page needs <Badge tone="brand">{ROLE_LABEL[needed]}</Badge> or above.
      </p>

      <p className="mt-3 text-xs leading-relaxed text-ink-tertiary">
        Access is granted per group, so the same person can run one desk and simply raise requests
        with another. Switch group from the header
        {isDevAuth() ? ', or switch identity from the menu in the top right' : ''}.
      </p>

      <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
        <Link to="/my">
          <Button variant="primary" size="sm">
            Go to my requests
          </Button>
        </Link>
        {/* DEV ONLY, and now actually enforced. Under a real provider this
            offered to sign somebody in as an administrator, which it cannot
            do and should not appear to: access comes from the directory and
            the roles table, not from clearing a token. */}
        {isDevAuth() && (
          <Button
            variant="secondary"
            size="sm"
            leading={<KeyRound className="h-3.5 w-3.5" />}
            onClick={() => {
              // Drop the local token so the app re-picks the highest-privilege
              // seeded identity on reload.
              try {
                localStorage.removeItem('esa.ticketing.token');
                localStorage.removeItem('esa.ticketing.tenant');
              } catch {
                /* ignore */
              }
              window.location.href = '/queue';
            }}
          >
            Sign in as platform admin
          </Button>
        )}
      </div>
    </div>
  );
}
